/*
 * Copyright (c) 2022 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, { useMemo, useCallback } from "react";
import {
	Box,
	Typography,
	Paper,
	Stack,
	Tooltip,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import { UserSlice } from "store";
import { UserPermissionDTO } from "models";
import { UseMaterialColors, UseUserRoles } from "lib/hooks";
import { SectionStyle } from "lib/styles/shared";
import { SectorPermissionChip } from "components/SectorPermissionChip";
import { PlantPermissionChip } from "components/PlantPermissionChip";
import { GetPermissionSortOrder } from "lib/permissions/PermissionUtils";

interface plantPermission {
	objectName: string;
	permissionCode?: string;
}

interface plantData {
	plantName: string;
	permissions: plantPermission[];
}

interface sectorAdminPermissionProps {
	sectorName: string;
	showSectorAdminChip?: boolean;
}

interface plantUserPermissionProps {
	sectorName: string;
	plantPermissions: plantData[];
	hasSectorAdmin?: boolean;
	defaultExpanded?: boolean;
}

/**
 * Sector Admin Permission component
 * Displays a non-collapsible card for Sector Admin permissions
 */
export function SectorAdminPermission({
	sectorName,
	showSectorAdminChip = true,
}: sectorAdminPermissionProps) {
	const materialColors = UseMaterialColors();

	return (
		<Box
			sx={{
				p: "16px 24px",
				borderRadius: 2,
				boxShadow: materialColors.isDark
					? "0 2px 8px rgba(0,0,0,0.2)"
					: "0 2px 8px rgba(0,0,0,0.08)",
				mb: 1.5,
				width: "100%",
				backgroundColor: materialColors.surface[1],
				minHeight: 56,
				height: 56,
				maxHeight: 56,
				display: "flex",
				alignItems: "center",
				transition: (theme) =>
					theme.transitions.create(["background-color", "box-shadow"], {
						duration: theme.transitions.duration.short,
						easing: theme.transitions.easing.easeOut,
					}),
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				<Typography
					variant="h5"
					sx={{
						fontWeight: 600,
						color: materialColors.onSurface,
						transition: (theme) =>
							theme.transitions.create("color", {
								duration: theme.transitions.duration.short,
								easing: theme.transitions.easing.easeOut,
							}),
					}}
				>
					{sectorName}
				</Typography>
				{showSectorAdminChip && (
					<>
						<Typography
							variant="body1"
							sx={{
								color: materialColors.onSurface,
								m: 0,
								transition: (theme) =>
									theme.transitions.create("color", {
										duration: theme.transitions.duration.short,
										easing: theme.transitions.easing.easeOut,
									}),
							}}
						>
							:
						</Typography>
						<SectorPermissionChip label="Sector Admin" />
					</>
				)}
			</Box>
		</Box>
	);
}

/**
 * Plant User Permission component
 * Displays a collapsible accordion for Plant User permissions with plant details
 */
export function PlantUserPermission({
	sectorName,
	plantPermissions,
	hasSectorAdmin = false,
	defaultExpanded = true,
}: plantUserPermissionProps) {
	const materialColors = UseMaterialColors();

	// Sort plants alphabetically, then permission chips by permission code order
	const sortedPlantPermissions = useMemo(() => {
		return [...plantPermissions]
			.sort((a, b) =>
				(a.plantName || "").localeCompare(b.plantName || "", undefined, {
					numeric: true,
					sensitivity: "base",
				})
			)
			.map((plantData) => ({
				...plantData,
				permissions: [...plantData.permissions].sort((a, b) => {
					const orderA = GetPermissionSortOrder(a.permissionCode);
					const orderB = GetPermissionSortOrder(b.permissionCode);
					return orderA - orderB;
				}),
			}));
	}, [plantPermissions]);

	return (
		<Accordion
			defaultExpanded={defaultExpanded}
			sx={{
				backgroundColor: materialColors.surface[1],
				borderRadius: 2,
				boxShadow: materialColors.isDark
					? "0 2px 8px rgba(0,0,0,0.2)"
					: "0 2px 8px rgba(0,0,0,0.08)",
				mb: 1.5,
				width: "100%",
				overflow: "hidden",
				minHeight: 56,
				"&:before": {
					display: "none",
				},
				"&.Mui-expanded": {
					mb: 3,
				},
				transition: (theme) =>
					theme.transitions.create(["background-color", "box-shadow"], {
						duration: theme.transitions.duration.short,
						easing: theme.transitions.easing.easeOut,
					}),
				"& .MuiCollapse-root": {
					transition: (theme) =>
						theme.transitions.create("height", {
							duration: theme.transitions.duration.standard,
							easing: theme.transitions.easing.easeInOut,
						}),
				},
				"& .MuiCollapse-wrapper": {
					transition: (theme) =>
						theme.transitions.create("height", {
							duration: theme.transitions.duration.standard,
							easing: theme.transitions.easing.easeInOut,
						}),
				},
				"& .MuiCollapse-wrapperInner": {
					transition: (theme) =>
						theme.transitions.create("height", {
							duration: theme.transitions.duration.standard,
							easing: theme.transitions.easing.easeInOut,
						}),
				},
				"& .MuiCollapse-entered": {
					overflow: "visible",
				},
			}}
		>
			<AccordionSummary
				expandIcon={
					<ExpandMoreIcon
						sx={{
							color: materialColors.primary,
						}}
					/>
				}
				sx={{
					px: 3,
					py: 2,
					minHeight: 56,
					height: 56,
					maxHeight: 56,
					borderRadius: "8px 8px 0 0",
					backgroundColor: materialColors.surface[1],
					"&.Mui-expanded": {
						minHeight: 56,
						borderRadius: "8px 8px 0 0",
					},
					"& .MuiAccordionSummary-content": {
						m: "12px 0",
						"&.Mui-expanded": {
							m: "12px 0",
						},
					},
					transition: (theme) =>
						theme.transitions.create(["min-height", "background-color"], {
							duration: theme.transitions.duration.standard,
							easing: theme.transitions.easing.easeInOut,
						}),
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						width: "100%",
					}}
				>
					<Typography
						variant="h5"
						sx={{
							fontWeight: 600,
							color: materialColors.onSurface,
							transition: (theme) =>
								theme.transitions.create("color", {
									duration: theme.transitions.duration.short,
									easing: theme.transitions.easing.easeOut,
								}),
						}}
					>
						{sectorName}
					</Typography>
					{hasSectorAdmin && (
						<>
							<Typography
								variant="body1"
								sx={{
									color: materialColors.onSurface,
									m: 0,
									transition: (theme) =>
										theme.transitions.create("color", {
											duration: theme.transitions.duration.short,
											easing: theme.transitions.easing.easeOut,
										}),
								}}
							>
								:
							</Typography>
							<SectorPermissionChip label="Sector Admin" />
						</>
					)}
					<Typography
						variant="body1"
						sx={{
							color: materialColors.onSurface,
							m: 0,
							transition: (theme) =>
								theme.transitions.create("color", {
									duration: theme.transitions.duration.short,
									easing: theme.transitions.easing.easeOut,
								}),
						}}
					>
						:
					</Typography>
					<SectorPermissionChip label="Plant User" />
				</Box>
			</AccordionSummary>
			{sortedPlantPermissions.length > 0 && (
				<AccordionDetails
					sx={{
						p: 0,
						pb: 1,
						borderRadius: "0 0 8px 8px",
						backgroundColor: materialColors.surface[1],
						transition: (theme) =>
							theme.transitions.create(["all", "background-color"], {
								duration: theme.transitions.duration.standard,
								easing: theme.transitions.easing.easeInOut,
							}),
					}}
				>
					<Stack
						spacing={2}
						sx={{
							px: 4.5,
							py: 2,
						}}
					>
						{sortedPlantPermissions.map((plantData) => (
							<Box
								key={plantData.plantName}
								sx={{
									display: "flex",
									alignItems: "center",
									flexWrap: "wrap",
									gap: 2.75,
									p: "16px 24px",
									borderRadius: 3,
									border: `1px solid ${materialColors.outline}`,
									backgroundColor: materialColors.surface[1],
									transition: (theme) =>
										theme.transitions.create(
											["background-color", "border-color"],
											{
												duration: theme.transitions.duration.short,
												easing: theme.transitions.easing.easeOut,
											}
										),
								}}
							>
								<Typography
									variant="subtitle1"
									sx={{
										fontWeight: 600,
										color: materialColors.onSurface,
										mr: 0.5,
										transition: (theme) =>
											theme.transitions.create("color", {
												duration: theme.transitions.duration.short,
												easing: theme.transitions.easing.easeOut,
											}),
									}}
								>
									{plantData.plantName}:
								</Typography>
								{plantData.permissions.map((perm, permIndex) => (
									<PlantPermissionChip
										key={`${perm.objectName}-${permIndex}`}
										objectName={perm.objectName}
										permissionCode={perm.permissionCode}
									/>
								))}
							</Box>
						))}
					</Stack>
				</AccordionDetails>
			)}
		</Accordion>
	);
}

export function Permissions() {
	const materialColors = UseMaterialColors();
	const { sectorAdminRoles, plantUserRoles } = UseUserRoles();
	const permissionList = useSelector(
		UserSlice.selectors.selectPermissionList
	) as UserPermissionDTO[];

	// Group sectors from both role types, ensuring one entry per sector
	const sectors = useMemo(() => {
		const sectorMap = new Map<
			string,
			{
				sectorName: string;
				hasSectorAdmin: boolean;
				hasPlantUser: boolean;
			}
		>();

		// Add sectors from Sector Admin roles
		sectorAdminRoles.forEach((role) => {
			const sectorName = role.containmentSectorName || "Unknown";
			if (!sectorMap.has(sectorName)) {
				sectorMap.set(sectorName, {
					sectorName,
					hasSectorAdmin: false,
					hasPlantUser: false,
				});
			}
			const sectorData = sectorMap.get(sectorName);
			if (sectorData) {
				sectorData.hasSectorAdmin = true;
			}
		});

		// Add sectors from Plant User roles
		plantUserRoles.forEach((role) => {
			const sectorName = role.containmentSectorName || "Unknown";
			if (!sectorMap.has(sectorName)) {
				sectorMap.set(sectorName, {
					sectorName,
					hasSectorAdmin: false,
					hasPlantUser: false,
				});
			}
			const sectorData = sectorMap.get(sectorName);
			if (sectorData) {
				sectorData.hasPlantUser = true;
			}
		});

		const sectorsArray = Array.from(sectorMap.values());
		return sectorsArray.sort((a, b) =>
			(a.sectorName || "").localeCompare(b.sectorName || "", undefined, {
				numeric: true,
				sensitivity: "base",
			})
		);
	}, [sectorAdminRoles, plantUserRoles]);

	// Group permissions by plant for Plant User roles, organized by sector
	const getPlantPermissionsForSector = useCallback((sectorName: string) => {
		const map = new Map<
			string,
			{
				plantName: string;
				permissions: Array<{ objectName: string; permissionCode?: string }>;
			}
		>();

		// Only process permissions if user has Plant User role for this sector
		const hasPlantUserRoleForSector = plantUserRoles.some((role) => 
			role.containmentSectorName === sectorName
		);

		if (hasPlantUserRoleForSector) {
			// Only add plants that have permissions from permissionList
			if (permissionList && permissionList.length > 0) {
				permissionList.forEach((perm: any) => {
					// Access fields directly (they're likely plain objects from API)
					const permSectorName = perm.sectorName || "";
					const plantName = perm.plantName || "Unknown";
					const objectName = perm.objectName || "Unknown";

					// Only include permissions for this sector
					if (permSectorName === sectorName && plantName && plantName !== "Unknown" && objectName && objectName !== "Unknown") {
						if (!map.has(plantName)) {
							map.set(plantName, {
								plantName,
								permissions: [],
							});
						}
						// Avoid duplicates
						const existing = map.get(plantName);
						if (existing && !existing.permissions.some(p => p.objectName === objectName)) {
							// Get permission code from various possible fields
							const permissionCode = perm.objectCode || perm.code || perm.objectId || "";
							existing.permissions.push({ objectName, permissionCode });
						}
					}
				});
			}
		}

		return map;
	}, [plantUserRoles, permissionList]);

	return (
		<Box
			sx={{
				p: 2,
				width: "100%",
				height: "100%",
			}}
		>
			{/* Header */}
			<Box sx={SectionStyle}>
				<Box sx={{ mb: 2 }}>
					<Tooltip
						title="View your assigned roles and permissions across all sectors and plants."
						arrow
					>
						<Typography
							variant="h4"
							gutterBottom
							sx={{
								display: "inline-block",
								cursor: "help",
							}}
						>
							Permissions
						</Typography>
					</Tooltip>
					<Typography
						variant="body2"
						sx={{
							color: materialColors.onSurfaceVariant,
							mt: 1,
							fontStyle: "italic",
							fontWeight: "bold",
						}}
					>
						Note: All Plant users will have Read-Only access to all plants within that sector.
					</Typography>
				</Box>
			</Box>

			{/* Sectors List */}
			{sectors.length > 0 && (
				<Stack spacing={3}>
					{sectors.map((sector) => {
						// Check if this sector has Sector Admin or Plant User
						const hasSectorAdmin = sector.hasSectorAdmin;
						const hasPlantUser = sector.hasPlantUser;
						const plantPermissionsMap = hasPlantUser ? getPlantPermissionsForSector(sector.sectorName) : new Map();
						const plantPermissions: Array<{ plantName: string; permissions: Array<{ objectName: string; permissionCode?: string }> }> = Array.from(plantPermissionsMap.values());
						
						// If only Sector Admin, show as non-collapsible card
						if (hasSectorAdmin && !hasPlantUser) {
							return (
								<SectorAdminPermission
									key={sector.sectorName}
									sectorName={sector.sectorName}
								/>
							);
						}
						
						// If Plant User (with or without Sector Admin), show as collapsible Accordion
						return (
							<PlantUserPermission
								key={sector.sectorName}
								sectorName={sector.sectorName}
								plantPermissions={plantPermissions}
								hasSectorAdmin={hasSectorAdmin}
								defaultExpanded={true}
							/>
						);
					})}
				</Stack>
			)}

			{/* Empty State */}
			{sectors.length === 0 && (
				<Paper
					sx={{
						p: 4,
						textAlign: "center",
						backgroundColor: materialColors.surface[1],
					}}
				>
					<Typography
						variant="body1"
						sx={{ color: materialColors.onSurfaceVariant }}
					>
						No permissions available
					</Typography>
				</Paper>
			)}
		</Box>
	);
}
